<i18n>
{
  "no": {
    "title": "Samtykke",
    "next": "Tillat samtykke",
    "nextCancel": "Avbryt",
    "infoMessage": "Følgende tillatelser blir bedt om av appen og ditt samtykke kreves for å gi den tilgang.",
    "cancelText": "Hvis du ikke godkjenner, klikker du avbryt og ingen informasjon vil bli delt av appen.",
    "accessText": "ønsker å få tilgang til kontoen din.",
    "loading": "Laster …",
    "maintenance": {
      "header": "Vedlikehold",
      "text": "Innlogging vil være utilgjengelig mens vi jobber med vedlikehold på våre digitale tjenester. Vi beklager eventuelle ulemper dette måtte medføre"
    }
  },
  "en": {
    "title": "Consent",
    "next": "Allow Consent",
    "nextCancel": "Cancel",
    "infoMessage": "The following permissions are requested by the app and your consent is required to provide it access.",
    "cancelText": "If you do not approve, click cancel and no information will be shared by the app.",
    "accessText": "wants to access your account.",
    "loading": "Loading …",
    "maintenance": {
      "header": "Maintenance",
      "text": "Login will be unavailable while we work on maintenance of our digital services. We apologize for any inconvenience this may cause"
    }
  }
}
</i18n>

<template>
  <div v-if="!isMaintenance" class="page">
    <FormWrapper>
      <div v-if="user">
        <TnHeading type="h1" size="s" align="center" class="margin-vertical-2xl">
          {{ t('title') }}
        </TnHeading>

        <TnParagraph size="s" class="margin-bottom-l text-center">
          <strong>{{ clientId }}</strong> {{ t('accessText') }}
        </TnParagraph>
        <TnParagraph size="s" class="margin-bottom-l text-center">
          {{ t('infoMessage') }}
        </TnParagraph>

        <ul id="scopeList" class="margin-bottom-l scope-list">
          <li v-for="scope in allScopes" :key="scope.name">
            <div class="scope-name">{{ scope.name }}</div>
            <TnSupportText status="info" :text="`- ${scope.description}`" />
          </li>
        </ul>

        <form action="/device/consent" method="get" class="margin-bottom-m">
          <TnButton id="btnGo" type="submit" primary block :text="t('next')" />
          <input v-if="userCode" type="hidden" name="userCode" :value="userCode" />
        </form>

        <form action="/device/cancel" method="get">
          <TnButton type="submit" secondary neutral block :text="t('nextCancel')" />
          <input v-if="userCode" type="hidden" name="userCode" :value="userCode" />
        </form>

        <TnSupportText status="info" class="margin-top-l cancel-text" :text="t('cancelText')" />
      </div>

      <TnNotificationInline v-else-if="error" horizontal status="critical" full-width>
        {{ t(error) }}
      </TnNotificationInline>

      <TnParagraph v-else class="loading-text">{{ t('loading') }}</TnParagraph>
    </FormWrapper>
  </div>

  <div v-else class="page">
    <TnHeading type="h1" size="m" align="center" class="margin-vertical-2xl">
      {{ t('maintenance.header') }}
    </TnHeading>
    <TnNotificationInline horizontal status="warning" full-width>
      {{ t('maintenance.text') }}
    </TnNotificationInline>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { User } from 'oidc-client-ts';
import {
  TnButton,
  TnHeading,
  TnNotificationInline,
  TnParagraph,
  TnSupportText,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import { EnvironmentService } from '@/environment';
import {
  createDeviceUserManager,
  DeviceFlowService,
  loadDeviceClient,
} from './device-flow.service';
import type { DeviceCodeRequest, ScopeInfo } from '@/types';

const { t } = useI18n();
const route = useRoute();

const environment = ref<Record<string, string>>({});
const user = ref<User | null>(null);
const userCode = ref<string | null>(null);
const clientId = ref<string | null>(null);
const identityResources = ref<ScopeInfo[]>([]);
const apiScopes = ref<ScopeInfo[]>([]);
/** Holds an i18n path (not a ready-made message), resolved with `t()` in the template. */
const error = ref<string | null>(null);

const isMaintenance = computed(() => environment.value.maintenance === 'true');
const allScopes = computed(() => [...identityResources.value, ...apiScopes.value]);

onMounted(async () => {
  environment.value = await EnvironmentService.getEnvironment();

  if (!route.query.state || !route.query.code) {
    error.value = 'generic.errors.requestError';
    console.error('Missing state param/code in return url?!');
    return;
  }

  const client = loadDeviceClient();
  if (!client) {
    error.value = 'generic.errors.requestError';
    console.error('Device flow session not found. Please restart the activation.');
    return;
  }

  try {
    const signedIn = await createDeviceUserManager(client).signinRedirectCallback();
    user.value = signedIn;
    userCode.value = signedIn.state as string;

    const request = await new DeviceFlowService().getDeviceRequest(userCode.value);

    if (!('clientId' in request)) {
      error.value = request.i18nPath;
      user.value = null;
      return;
    }

    const details = request as DeviceCodeRequest;
    clientId.value = details.clientId;
    identityResources.value = details.identityResources ?? [];
    apiScopes.value = details.apiScopes ?? [];
  } catch (err) {
    // Never surface raw exception text to the user - log it, show the generic message.
    error.value = 'generic.errors.requestError';
    console.error(err);
  }
});
</script>

<style scoped>
.scope-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
}

.scope-name {
  font-size: 18px;
  line-height: 28px;
  color: #000;
}

.cancel-text {
  display: block;
  text-align: center;
}

.loading-text {
  padding-block: 32px;
  text-align: center;
}
</style>
